<template>
    <button
        type="button"
        class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
        data-testid="mic-test-button"
        title="Play your mic back to yourself to verify audio is being captured"
        aria-label="Test microphone"
        @click="open">
        🎚 Test mic
    </button>
    <dialog ref="dialogElement" data-testid="mic-test-dialog" class="rounded-xl bg-white p-8 text-slate-800 focus-visible:outline-0" @close="teardownStream">
        <div class="flex w-[420px] max-w-full flex-col gap-4">
            <div>
                <h2 class="text-lg font-semibold">Test your microphone</h2>
                <p class="mt-1 text-sm text-slate-600">
                    Speak normally — you should hear yourself with a short delay and the level meter should move. If not, switch to a different mic below.
                </p>
            </div>

            <div v-if="error" class="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="mic-test-error">
                {{ error }}
            </div>

            <label v-else for="mic-test-select" class="block text-xs font-medium text-slate-500">
                Microphone
                <select id="mic-test-select" v-model="selectedMicId" class="mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm" data-testid="mic-test-select">
                    <option v-for="mic in mics" :key="mic.deviceId" :value="mic.deviceId">
                        {{ mic.label || `Microphone (${mic.deviceId.slice(0, 6)}…)` }}
                    </option>
                </select>
            </label>

            <div>
                <div class="text-xs font-medium text-slate-500">Input level</div>
                <div class="mt-1 flex flex-row gap-1" role="presentation" data-testid="mic-test-level">
                    <div v-for="i in LEVEL_CELLS" :key="i" class="h-5 w-2 rounded-sm" :class="i <= audioLevel ? 'bg-emerald-500' : 'bg-slate-200'"></div>
                </div>
                <p class="mt-2 text-xs text-slate-500">Selection is saved automatically — voice calls will use the same mic.</p>
            </div>

            <div class="flex justify-end">
                <button type="button" class="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700" data-testid="mic-test-close" @click="close">
                    Done
                </button>
            </div>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from "vue";

import { PREFERRED_MIC_KEY } from "./useVoiceSession";

// Number of bar-cells in the level meter.
const LEVEL_CELLS = 25;
// Delay between mic input and speaker output. Long enough that "speak,
// then pause" makes the loopback obvious without becoming annoying.
const PLAYBACK_DELAY_SECONDS = 0.3;
// Voice rarely fills the full FFT range, so we scale the meter up so the
// bars actually move at conversational volume.
const LEVEL_BOOST = 2;
// "default" / "communications" duplicate concrete devices on most OSes
// and don't survive a browser restart cleanly. Hide them when there's
// at least one real device.
const PSEUDO_DEVICE_IDS = new Set(["default", "communications"]);

const dialogElement = ref<HTMLDialogElement | null>(null);
const mics = ref<MediaDeviceInfo[]>([]);
const selectedMicId = ref<string>("");
const audioLevel = ref<number>(0);
const error = ref<string | null>(null);

let stream: MediaStream | null = null;
let audioContext: AudioContext | null = null;
let nodes: AudioNode[] = [];
let rafId: number | null = null;
// Monotonic guard against overlapping starts. `open()` and the
// selectedMicId watcher can both call startPlayback() around the same
// time; without this, two getUserMedia() streams race to assign `stream`
// and the loser is orphaned — its mic track never stops, so the mic
// indicator stays lit after the dialog closes. Every start claims an
// epoch; any start (or teardown) that supersedes it forces the stale one
// to release its own stream instead of leaking it.
let playbackEpoch = 0;

watch(selectedMicId, async (newId, oldId) => {
    if (!newId || newId === oldId) {
        return;
    }
    localStorage.setItem(PREFERRED_MIC_KEY, newId);
    await startPlayback();
});

const open = async () => {
    error.value = null;
    audioLevel.value = 0;
    dialogElement.value?.showModal();

    try {
        // Prompt for permission once so device labels populate. Stop
        // the throwaway stream immediately — we'll re-open with the
        // chosen deviceId.
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
        tmp.getTracks().forEach((t) => t.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === "audioinput" && d.deviceId !== "");
        const concrete = audioInputs.filter((d) => !PSEUDO_DEVICE_IDS.has(d.deviceId));
        mics.value = concrete.length > 0 ? concrete : audioInputs;

        if (mics.value.length === 0) {
            error.value = "No microphones found.";
            return;
        }

        const saved = localStorage.getItem(PREFERRED_MIC_KEY);
        if (saved && mics.value.find((m) => m.deviceId === saved)) {
            selectedMicId.value = saved;
        } else {
            selectedMicId.value = mics.value[0].deviceId;
            localStorage.setItem(PREFERRED_MIC_KEY, selectedMicId.value);
        }

        await startPlayback();
    } catch (err) {
        if (err instanceof DOMException && err.name === "NotAllowedError") {
            error.value = "Microphone permission denied. Allow access in your browser settings and try again.";
        } else {
            error.value = err instanceof Error ? err.message : String(err);
        }
    }
};

const close = () => {
    // Closing the dialog fires its native `close` event, which runs
    // teardownStream — covering this button, the Escape key, and any
    // programmatic close from a single place.
    dialogElement.value?.close();
};

const startPlayback = async () => {
    if (!selectedMicId.value) {
        return;
    }
    // Release any existing/in-flight stream first, then claim the latest epoch.
    await teardownStream();
    const epoch = ++playbackEpoch;
    const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selectedMicId.value } },
    });
    // A newer start or a teardown ran while we awaited getUserMedia — abandon
    // this stream rather than overwriting (and leaking) the current one.
    if (epoch !== playbackEpoch) {
        newStream.getTracks().forEach((t) => t.stop());
        return;
    }
    stream = newStream;
    audioContext = new AudioContext();

    const source = audioContext.createMediaStreamSource(stream);
    const delay = audioContext.createDelay();
    delay.delayTime.value = PLAYBACK_DELAY_SECONDS;
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    nodes = [source, delay, analyser];

    source.connect(analyser);
    source.connect(delay);
    delay.connect(audioContext.destination);

    const buf = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
        if (!audioContext) {
            return;
        }
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
            sum += buf[i];
        }
        const avg = sum / buf.length;
        audioLevel.value = Math.min(LEVEL_CELLS, Math.round((avg / 255) * LEVEL_CELLS * LEVEL_BOOST));
        rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
};

const teardownStream = async () => {
    // Invalidate any startPlayback() currently awaiting getUserMedia so it
    // releases its stream instead of installing it after we've torn down.
    playbackEpoch++;
    if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
    if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
    }
    nodes.forEach((n) => n.disconnect());
    nodes = [];
    if (audioContext) {
        await audioContext.close();
        audioContext = null;
    }
    audioLevel.value = 0;
};

onUnmounted(() => {
    teardownStream();
});
</script>

<style type="postcss">
dialog[data-testid="mic-test-dialog"]::backdrop {
    background-color: #000;
    opacity: 0.4;
}
</style>
